import { ReactNode, RefObject, useState } from 'react'

import { SizeOptions } from '../../../types/types'
import { AstraPopover } from '../../AstraPopover/AstraPopover'
import { AstraMultiSelectCollapsable, AstraMultiSelectCollapsableProps } from './components/AstraMultiSelectCollapsable'
import { useMultiSelectData } from './hooks/useMultiSelectData'

// Overlap the expanded popover onto the collapsed trigger; matches the trigger height per size.
const BOTTOM_HERO_POPOVER_OFFSET: Record<SizeOptions, number> = {
    small: -22,
    medium: -30,
    large: -38,
}

export type AstraMultiSelectProps<T> = Omit<
    AstraMultiSelectCollapsableProps<T>,
    | 'mode'
    | 'selectedOptions'
    | 'groupedOptionValues'
    | 'openGroups'
    | 'onOpenGroupsChange'
    | 'filteredOptions'
    | 'valueToGroupLabel'
    | 'fullySelectedGroups'
    | 'groups'
> & {
    values: T[]
    expandedGroups?: boolean
    dropdownIcon?: ReactNode
    /** Whether group options are selectable (select/unselect all in group) */
    disableGroupSelection?: boolean
    /** Whether the multiselect is rendered inline (full control visible) or as a popover with a collapsed value display */
    displayMode?: 'popover' | 'inline'
    'data-test'?: string
    popoverContainer?: RefObject<HTMLDivElement | null>
}

export function AstraMultiSelect<T>({
    options,
    values,
    onChange,
    placeholder = 'Velg...',
    isDisabled,
    readOnly,
    menuPlacement = 'auto',
    expandedGroups = false,
    dropdownIcon,
    displayMode = 'popover',
    disableGroupSelection,
    size = 'medium',
    'data-test': dataTest,
    popoverContainer,
    ...restProps
}: AstraMultiSelectProps<T>) {
    const [popoverOpen, setPopoverOpen] = useState(false)

    const { selectedOptions, groups, valueToGroupLabel, fullySelectedGroups, filteredOptions, openGroups, setOpenGroups, groupedOptionValues } =
        useMultiSelectData(options, values, expandedGroups)

    const renderVariant = (mode: AstraMultiSelectCollapsableProps<T>['mode'], onMenuClose?: () => void) => (
        <AstraMultiSelectCollapsable
            options={options}
            onChange={onChange}
            placeholder={placeholder}
            isDisabled={isDisabled}
            readOnly={readOnly}
            dropdownIcon={dropdownIcon}
            expandedGroups={expandedGroups}
            menuPlacement={menuPlacement}
            mode={mode}
            selectedOptions={selectedOptions}
            groupedOptionValues={groupedOptionValues}
            disableGroupSelection={disableGroupSelection}
            openGroups={openGroups}
            onOpenGroupsChange={setOpenGroups}
            filteredOptions={filteredOptions}
            onMenuClose={onMenuClose}
            groups={groups}
            valueToGroupLabel={valueToGroupLabel}
            fullySelectedGroups={fullySelectedGroups}
            size={size}
            {...restProps}
        />
    )

    if (displayMode === 'inline') return renderVariant('default')

    return (
        <div>
            <AstraPopover
                isOpen={popoverOpen}
                onOpenChange={setPopoverOpen}
                popoverContent={renderVariant('expanded', () => setPopoverOpen(false))}
                noPadding
                disabled={isDisabled || readOnly}
                isModal={false}
                matchTriggerWidth={true}
                className="z-elevated rounded-none border-none bg-transparent shadow-none"
                side="bottom"
                sideOffset={BOTTOM_HERO_POPOVER_OFFSET[size]}
                container={popoverContainer?.current ?? undefined}
            >
                <div onClick={() => setPopoverOpen(true)} data-test={dataTest}>
                    {renderVariant('collapsed')}
                </div>
            </AstraPopover>
        </div>
    )
}
